import { assetBasicFields, renderBasicFormHTML } from '../form-config.js';
import { loadTenants } from '../../../modules/tenants.js';
import { createAsset, getUnassignedDevices } from '../api.js';
import { resolveFieldLabels, CUSTOM_TEXT_FIELDS, customTextFields, FIXED_TEXT_FIELDS, fixedTextFields } from '../field-labels.js';
import { loadAssetFields, fieldVisibleForTenant, effectiveFieldLabel } from '../asset-fields.js';
import { escapeHtml } from '../../../utils/escape.js';
import { can, getCurrentUser } from '../../../modules/rbac.js';
import { deviceTypeLabel } from '../../devices/device-type.js';
import { scopeTenantParam } from '../../../modules/tenant-scope.js';

// The fields actually rendered in the modal (text fields + registry dropdowns +
// status + tenant). Built per open so it reflects the current registry.
let currentModalFields = [];

let modalInstance = null;   // ← this line must exist!

export function createNewAssetModal() {

  if (document.getElementById('newAssetModal')) return;

  const modalHTML = `
    <div class="modal fade" id="newAssetModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create New Asset</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" id="new-asset-form-body">
            <!-- Dynamically filled -->
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" id="btn-create-asset" class="btn btn-success">
              <i class="fa-solid fa-plus me-1"></i>Create Asset
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', modalHTML);

  const modalEl = document.getElementById('newAssetModal');
  modalInstance = new bootstrap.Modal(modalEl);

  // Helper to (re-)render the form
  const renderForm = async () => {
    try {
      const [tenants, regFields] = await Promise.all([loadTenants(), loadAssetFields().catch(() => [])]);
      const body = document.getElementById('new-asset-form-body');
      if (!body) return;

      // Build the field list: text fields, then registry dropdowns (Asset type,
      // Engine type, …), then status + tenant. Registry fields replace the old
      // static 'type' field.
      const textAndStatus = assetBasicFields.filter(f => !['type', 'tenantId'].includes(f.key) && f.key !== 'status');
      const statusField = assetBasicFields.find(f => f.key === 'status');
      const tenantField = assetBasicFields.find(f => f.key === 'tenantId');
      const regAsFields = regFields.map(f => ({
        key: f.fieldId, label: f.label, type: 'select', registry: true,
        options: (f.options || []).map(o => ({ value: o.value, text: o.label })),
        _def: f,
      }));
      // Two optional free-text custom fields (title + on/off follow the tenant).
      const customFieldDefs = CUSTOM_TEXT_FIELDS.map(f => ({ key: f.key, label: f.defaultLabel, type: 'text', custom: true }));
      // Fixed standard fields (canonical label, on/off follows the tenant).
      const fixedFieldDefs = FIXED_TEXT_FIELDS.map(f => ({ key: f.key, label: f.label, type: 'text', custom: true }));
      currentModalFields = [...textAndStatus, ...regAsFields, ...customFieldDefs, ...fixedFieldDefs, statusField, tenantField].filter(Boolean);

      // Global tenant scope: under a single scope the new asset's tenant is
      // prefilled with the scope tenant and the field is hidden — otherwise the
      // freshly created asset would instantly vanish from the current view.
      const scopeTid = scopeTenantParam();
      const scopePinned = scopeTid && scopeTid !== 'unassigned' && tenants.some(t => t.tenantId === scopeTid);
      body.innerHTML = renderBasicFormHTML(currentModalFields, scopePinned ? { tenantId: scopeTid } : {}, tenants);
      if (scopePinned) body.querySelector('[data-field-row="tenantId"]')?.classList.add('d-none');

      // Optional device-attach picker — only for roles that may assign devices.
      // Lists just UNASSIGNED devices the caller can see (server-scoped: a tenant
      // role sees only its own tenant's devices, a platform role sees all, incl.
      // ownerless). Whichever devices are picked are assigned to the new asset on
      // create and inherit its tenant (server stamps it — see handlers/assets +
      // syncAssetDeviceAssignmentsV2), which matters when a superadmin attaches an
      // ownerless device to a tenant's asset.
      const groups = getCurrentUser().groups || [];
      if (can(groups, 'assignDevice')) {
        body.insertAdjacentHTML('beforeend', `
          <hr class="my-3">
          <div class="mb-1">
            <label class="form-label fw-semibold mb-1">Attach devices <span class="fw-normal text-muted small">(optional)</span></label>
            <select id="new-asset-devices" class="form-select" multiple style="width:100%"></select>
            <div class="form-text">Only unassigned devices you can access are listed. Each one is paired to this asset and inherits its tenant.</div>
          </div>`);
        try {
          const unassigned = await getUnassignedDevices();
          $('#new-asset-devices').select2({
            placeholder: 'Search by deviceId, serial, IMEI, type…',
            dropdownParent: modalEl,
            width: '100%',
            allowClear: true,
            data: unassigned.map(d => {
              const sn = d.serialNumber || d.imei || 'N/A';
              return {
                id: d.deviceId,
                text: `${deviceTypeLabel(d)} · ${d.deviceId} · SN ${sn}${d.lastSeen ? ' · seen ' + new Date(d.lastSeen).toLocaleDateString() : ''}`,
              };
            }),
          });
        } catch (e) {
          console.warn('[Modal] unassigned devices load failed', e);
        }
      }

      // Init select2 on the tenant + every registry dropdown.
      $('#asset-tenantId').select2({ placeholder: 'Select Tenant...', width: '100%', dropdownParent: modalEl, allowClear: true });
      regAsFields.forEach(f => $(`#asset-${f.key}`).select2({ width: '100%', dropdownParent: modalEl }));

      // Labels + visibility follow the chosen tenant.
      const applyTenant = (tid) => {
        const t = tenants.find(x => x.tenantId === tid) || null;
        const L = resolveFieldLabels(t);
        ['label', 'reference', 'serial'].forEach(k => {
          const el = body.querySelector(`[data-field-label="${k}"]`);
          if (el) el.innerHTML = `${escapeHtml(L[k])}${k === 'label' ? ' <span class="text-danger">*</span>' : ''}`;
        });
        regAsFields.forEach(f => {
          const labelEl = body.querySelector(`[data-field-label="${f.key}"]`);
          if (labelEl) labelEl.textContent = effectiveFieldLabel(f._def, t);
          const rowEl = body.querySelector(`[data-field-row="${f.key}"]`);
          if (rowEl) rowEl.classList.toggle('d-none', !fieldVisibleForTenant(f._def, t));
        });
        // Custom text fields: tenant title + on/off (hidden when disabled).
        customTextFields(t).forEach(cf => {
          const labelEl = body.querySelector(`[data-field-label="${cf.key}"]`);
          if (labelEl) labelEl.textContent = cf.label;
          const rowEl = body.querySelector(`[data-field-row="${cf.key}"]`);
          if (rowEl) rowEl.classList.toggle('d-none', !cf.enabled);
        });
        // Fixed standard fields: on/off only (label is canonical, set at render).
        fixedTextFields(t).forEach(ff => {
          const rowEl = body.querySelector(`[data-field-row="${ff.key}"]`);
          if (rowEl) rowEl.classList.toggle('d-none', !ff.enabled);
        });
      };

      $('#asset-tenantId').on('change', function () { applyTenant($(this).val()); });
      // Reset the picker on each open — but under a single tenant scope keep the
      // pinned scope tenant (the field is hidden then; resetting to '' silently
      // created tenant-less assets that vanished from the user's view).
      const initialTid = scopePinned ? scopeTid : '';
      $('#asset-tenantId').val(initialTid).trigger('change');
      applyTenant(initialTid);
    } catch (e) {
      console.warn('[Modal] form render failed', e);
    }
  };

  // Render immediately after modal creation (avoids a race condition)
  renderForm();

  // Re-render on every open (fresh tenant list)
  modalEl.addEventListener('shown.bs.modal', renderForm);

  // Very robust submit handler — never .value on null
  // Very robust submit handler — never .value on null + clean tenantId handling
  document.getElementById('btn-create-asset').addEventListener('click', async () => {
    const btn = document.getElementById('btn-create-asset');
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Creating...`;

    try {
      const data = {};

      (currentModalFields.length ? currentModalFields : assetBasicFields).forEach(field => {
        const el = document.getElementById(`asset-${field.key}`);
        if (!el) return;

        if (field.key === 'tenantId') {
          const val = $('#asset-tenantId').val();
          // Only set when a tenant was actually chosen ('' or null = unassigned)
          if (val && val !== '') {
            data.tenantId = val;
          }
        } else {
          data[field.key] = (el.value || '').trim();
        }
      });

      if (!data.label) {
        throw new Error('Label is required');
      }

      // Attach any picked devices — the server assigns them to the new asset and
      // stamps them with its tenant. Only unassigned, accessible devices are
      // offered, so no extra client-side guard is needed.
      const devSel = document.getElementById('new-asset-devices');
      if (devSel) {
        const ids = $('#new-asset-devices').val() || [];
        if (ids.length) data.assignedDeviceIds = ids;
      }

      const result = await createAsset(data);
      window.showToast('Asset created successfully', 'success');

      modalInstance.hide();

      if (typeof window.refreshAssets === 'function') {
        window.refreshAssets();
      }

    } catch (err) {
      console.error(err);
      window.showToast(err.message || 'Failed to create asset', 'danger');
    } finally {
      btn.disabled = false;
      btn.innerHTML = original;
    }
  });

  console.log('✅ New Asset Modal (robust + Unassigned default) created');
}